import { FrontendTenantConfig } from '../types';

/**
 * WCS — West Coast Surrogacy. FE-relevant subset of 02's tenant contract; see `isc/tenant.ts`'s
 * header for why these values are restated rather than imported.
 *
 * BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts
 */
export const wcsTenant: FrontendTenantConfig = {
  slug: 'wcs',
  // BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts:19
  displayName: 'West Coast Surrogacy',
  phone: {
    // G1 — the BE registry holds a THIRD format, `(877) 492-7411` (wcs.tenant.ts:27) — the drift
    // D9/R2 accept.
    display: '877-492-7411',
    tel: '18774927411',
  },
  urls: {
    // BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts:45-49
    terms: 'https://www.westcoastsurrogacy.com/terms-of-use/',
    privacy: 'https://www.westcoastsurrogacy.com/content/privacy-notice-2024.pdf',
    requirements: 'https://www.westcoastsurrogacy.com/become-a-surrogate-mother/surrogate-mother-requirements',
    contactUs: 'https://www.westcoastsurrogacy.com/contact-us/',
    marketingBase: 'https://www.westcoastsurrogacy.com',
  },
  pdf: {
    // §4A.5 — verified against `origin/wcs` (f07eb06).
    creator: 'wcs',
    producer: 'wcs',
    applicationCoverLogoWidth: 125,
    questionnaireCoverLogoWidth: 116,
  },
  head: {
    // §4A.5 — verified against `origin/wcs` (f07eb06) public/index.html.
    title: 'West Coast Surrogacy',
    description: 'Welcome to West Coast Surrogacy portal',
  },
  // SAA-61/05 D3 — authored inline, machine-diffed against the BE registry (R4) by
  // `print-frontend-workflow.ts` + `scripts/check-tenant-alignment.mjs`. Byte-identical to
  // `apps/backend/libs/tenant/src/tenants/wcs.tenant.ts`, same order (§3.5).
  workflow: {
    // BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts:66-76 (statuses)
    statuses: [
      'lead',
      'applicants',
      // SAA-112 — created by "New App"; outside `editableStatuses` (D10, locked at birth).
      'newApplicant',
      'completed',
      'potential',
      'available',
      'matched',
      'delivered',
      'comeBackLater',
      'purged',
      'disqualified',
      // SAA-80 (2026-08-07) — Streak's stage list is the source of truth per tenant. WCS's pipeline
      // carries 22 stages `[MEASURED]`; only these 10 had an AP status, so 12 stages produced no status
      // change at all (BA-QUESTIONS 3a). Appended in STAGE-KEY order (5012..5023) — that is the display
      // order too. `prescreening`/`referrals`/`bmiPotentials` are WCS-only: keys 5020-5023 mean
      // DIFFERENT stages in each agency, so these must NOT be copied from ISC.
      'matchFellOut',
      'exploring',
      'legal',
      'ivfMock',
      'postTransfer',
      'obCare',
      'postDelivery',
      'doNotReachOut',
      'prescreening',
      'referrals',
      'contactUs',
      'bmiPotentials',
    ],
    // BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts:78 (editableStatuses — WCS
    // hardcodes `=== applicants` rather than shipping a const, F1/F5)
    editableStatuses: ['applicants'],
    // BE source: apps/backend/libs/tenant/src/tenants/wcs.tenant.ts (intendedParentTypes) —
    // no `Domestic`: WCS's pipeline has no such option.
    intendedParentTypes: [
      'alreadyHaveChildren',
      'hetero',
      'homo',
      'singleMale',
      'singleFemale',
      'singleGayMale',
      'singleGayFemale',
      'transgender',
    ],
    // SAA-122 D2 — the GC lead form's six options, in AC-1 order, byte-identical on both tenants
    // (mirrors apps/backend/libs/tenant/src/tenants/wcs.tenant.ts — check-tenant-alignment.mjs).
    referralOptions: ['referred', 'socialMedia', 'google', 'chatgpt', 'claude', 'other'],
  },
  // SAA-184 — WCS variant of matching now ships; BE `features.matchingEnabled` gates the API side.
  matchingToolEnabled: true,
};
